import { useState } from 'react'
import { useAuth0 } from '@/auth/index'
import useConversationsStore from '@/stores/useConversationsStore'
import { useAwsCredentialsManager } from '../context/AWS-credentials.context'
import { pinConversation as pinConversationAPI, unpinConversation as unpinConversationAPI } from '../services/conversations-api'

interface UsePinConversationProps {
  conversationId: string
  conversationTitle: string
}

interface UsePinConversationReturn {
  isLoading: boolean
  error: Error | null
  isPinned: boolean
  togglePin: () => Promise<void>
}

const usePinConversation = ({
  conversationId,
  conversationTitle,
}: UsePinConversationProps): UsePinConversationReturn => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const { user } = useAuth0()
  const { applicationId, credentials } = useAwsCredentialsManager()

  // Store actions and selectors
  const conversation = useConversationsStore((state) => state.getConversation(conversationId))
  const pinConversationStore = useConversationsStore((state) => state.pinConversation)
  const unpinConversationStore = useConversationsStore((state) => state.unpinConversation)
  const rollbackPinState = useConversationsStore((state) => state.rollbackPinState)
  const updateConversationPinMetadata = useConversationsStore((state) => state.updateConversationPinMetadata)

  const isPinned = conversation?.isPinned || false

  const togglePin = async (): Promise<void> => {
    if (isLoading) return

    setIsLoading(true)
    setError(null)

    // Store current state for rollback
    const previousState = { 
      isPinned: isPinned,
      originalCreatedAt: conversation?.createdAt
    }

    try {
      if (isPinned) {
        // Unpinning: optimistic update
        unpinConversationStore(conversationId)

        // Call API
        const unpinResponse = await unpinConversationAPI({
          conversationId,
          email: user.email,
          applicationId: applicationId!,
          accessKeyId: credentials?.AccessKeyId,
          secretAccessKey: credentials?.SecretAccessKey,
          sessionToken: credentials?.SessionToken,
        })

        // Restore original createdAt from response creationTime
        if (unpinResponse.creationTime) {
          const originalCreatedAt = new Date(unpinResponse.creationTime).getTime()
          updateConversationPinMetadata(conversationId, false, originalCreatedAt)
        }
      } else {
        // Pinning: check limit first, then optimistic update
        const pinResult = pinConversationStore(conversationId)
        
        if (!pinResult.success) {
          throw new Error(pinResult.error || 'Failed to pin conversation')
        }

        // Call API - map createdAt to creationTime
        const pinResponse = await pinConversationAPI({
          conversationId,
          email: user.email,
          applicationId: applicationId!,
          title: conversationTitle,
          creationTime: conversation?.createdAt ? new Date(conversation.createdAt).toISOString() : new Date().toISOString(),
          accessKeyId: credentials?.AccessKeyId,
          secretAccessKey: credentials?.SecretAccessKey,
          sessionToken: credentials?.SessionToken,
        })

        // Update conversation with pin metadata from response
        const pinTime = new Date(pinResponse.startTime).getTime()
        updateConversationPinMetadata(conversationId, true, pinTime)
      }
    } catch (err) {
      // Rollback optimistic update on failure
      rollbackPinState(conversationId, previousState)

      const errorMessage = err instanceof Error ? err.message : 'Network or other error during pin/unpin'
      console.error(`Error ${isPinned ? 'unpinning' : 'pinning'} conversation:`, errorMessage)
      setError(new Error(errorMessage))
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    error,
    isPinned,
    togglePin,
  }
}

export default usePinConversation